import { permissionsSelectors } from 'ducks/permissions'
import { isEmpty } from 'lodash'
import { Box, Typography } from 'opensolar-ui'
import { ComponentCost } from 'pages/ordering/type'
import { useContext, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from 'util/featureFlags'
import { DistributorTradePricingContext, DistributorTradePricingContextType } from '../HardwareSelectorCore'
import { DistributorDataTypeV2 } from '../types'
import useTotalStockAcrossSuppliers from '../useTotalStockAcrossSuppliers'
import AvailableStocksButton from './AvailableStocksButton'
import { getStockTextAndColor } from './stockText'
import { getStockStatusMessagesPerDistributor, StockStatusMessagesType } from './stockUtils'

interface QuantityBlockProps {
  distributors: DistributorDataTypeV2[]
  costSource?: ComponentCost | null
  productCode?: string
  productName?: string
}

const QuantityBlock: React.FC<QuantityBlockProps> = ({ distributors, costSource, productCode, productName }) => {
  const translate = useTranslate()
  const distributorTradePricing =
    useContext<DistributorTradePricingContextType>(DistributorTradePricingContext).distributorTradePricing
  const multiDistributor = useFeatureFlag('design_multi_distributor', 'on')
  const hardwareSourceSelectionPermission = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'hardware_source_selection')
  )

  const { availableQuantity, isGlobalOutOfStock, showIsInStockElsewhere } = useTotalStockAcrossSuppliers(
    distributors,
    costSource?.source
  )

  const stockStatusPerDistributor: StockStatusMessagesType[] = useMemo(() => {
    const stockStatusMessages = getStockStatusMessagesPerDistributor({
      distributors,
      availableQuantity,
      translate,
    })
    if (!costSource?.source) {
      return stockStatusMessages
    }
    return stockStatusMessages.filter(({ distributor }) => distributor === costSource.source)
  }, [distributors, costSource, availableQuantity, translate])

  const { stockText, stockTextColor } = getStockTextAndColor(
    stockStatusPerDistributor,
    availableQuantity,
    costSource?.source,
    distributors
  )

  if (
    isEmpty(stockStatusPerDistributor) ||
    (multiDistributor && costSource?.source === 'component' && hardwareSourceSelectionPermission.allowEdit)
  )
    return null

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      color={stockTextColor}
      gridGap={8}
      flexWrap="wrap"
    >
      <Typography variant="body2" style={{ wordBreak: 'break-word' }}>
        {translate(stockText)}
      </Typography>
      {!isGlobalOutOfStock && (
        <AvailableStocksButton
          data={distributors}
          selectedDistributor={costSource?.source}
          inStockElsewhere={showIsInStockElsewhere}
          productCode={productCode}
          productName={productName}
          amplitudeEventType="Component Selector Activity"
        />
      )}
    </Box>
  )
}

export default QuantityBlock
